import React from 'react'
import { css, cx } from '@emotion/css'
import { codeCss } from '../../legacy-design-system/text'
import { FormattedMessage } from 'react-intl'
import clipboardCopy from 'clipboard-copy'
import { BasicDialogBody, Dialog } from '../../legacy-design-system/modal'
import { Button } from '@contrast-security-inc/design-system-buttons'

const sampleAPIRequestCss = cx(
  codeCss,
  css`
    word-break: break-word;
    white-space: pre-wrap;
  `
)

export interface OrganizationProps {
  readonly id: string
  readonly name: string
  readonly isSuperadmin?: boolean
}

export interface Props {
  readonly apiKey: string
  readonly organization: OrganizationProps
  readonly contrastURL: string
  readonly authHeader: string
  readonly acceptHeader: string
  readonly onCopyAPIRequest: () => void
}

export interface State {
  readonly isRequestDialogOpen: boolean
}

export class SampleApiRequest extends React.Component<Props, State> {
  public state: State = {
    isRequestDialogOpen: false
  }

  public render() {
    const {
      organization,
      acceptHeader,
      authHeader,
      apiKey,
      contrastURL,
      onCopyAPIRequest
    } = this.props
    const { isRequestDialogOpen } = this.state

    // Wait for the contrast URL to become available.
    if (!contrastURL) {
      return null
    }

    const sampleRequest = organization.isSuperadmin
      ? getSuperadminSampleRequest(
          authHeader,
          apiKey,
          acceptHeader,
          contrastURL
        )
      : getSampleRequest(
          organization.id,
          authHeader,
          apiKey,
          acceptHeader,
          contrastURL
        )

    return (
      <div>
        <Button onClick={this.openSampleAPIRequestModal}>
          <FormattedMessage id={'action_profile_generateSampleRequest'} />
        </Button>

        <Dialog
          isOpen={isRequestDialogOpen}
          onCancel={this.closeSampleAPIRequestModal}
          renderTitle={() => <FormattedMessage id="title_sampleAPIRequest" />}
        >
          <BasicDialogBody
            body={<span className={sampleAPIRequestCss}>{sampleRequest}</span>}
            confirmButtonText={<FormattedMessage id="action_copy" />}
            cancelButtonText={<FormattedMessage id="action_cancel" />}
            onCancel={this.closeSampleAPIRequestModal}
            onConfirm={() => {
              this.closeSampleAPIRequestModal()
              this.copyToClipboard(sampleRequest, onCopyAPIRequest).catch(
                (err) => console.error(err)
              )
            }}
          />
        </Dialog>
      </div>
    )
  }

  private openSampleAPIRequestModal = () => {
    this.setState({
      isRequestDialogOpen: true
    })
  }

  private closeSampleAPIRequestModal = () => {
    this.setState({
      isRequestDialogOpen: false
    })
  }

  private readonly copyToClipboard = async (
    str: string,
    onCopyFn: () => void
  ) => {
    await clipboardCopy(str)
    onCopyFn()
  }
}

export const getSampleRequest = (
  orgID: string,
  authHeader: string,
  apiKey: string,
  acceptHeader: string,
  contrastURL: string
) => {
  if (contrastURL?.endsWith('/')) {
    contrastURL = contrastURL.slice(0, -1)
  }

  return `curl -X GET \\
     ${contrastURL}/api/ng/${orgID}/applications/ \\
     -H 'Authorization: ${authHeader}' \\
     -H 'API-Key: ${apiKey}' \\
     -H 'Accept: ${acceptHeader}'`
}

export const getSuperadminSampleRequest = (
  authHeader: string,
  apiKey: string,
  acceptHeader: string,
  contrastURL: string
) => {
  if (contrastURL?.endsWith('/')) {
    contrastURL = contrastURL.slice(0, -1)
  }

  return `curl -X GET \\
     ${contrastURL}/api/ng/superadmin/applications/ \\
     -H 'Authorization: ${authHeader}' \\
     -H 'API-Key: ${apiKey}' \\
     -H 'Accept: ${acceptHeader}'`
}
